import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faTriangleExclamation } from '@fortawesome/sharp-solid-svg-icons';
import { useLicenseStatus } from '../hooks/useLicenseStatus';
import { isOnPremMode } from '../utils/runtimeConfig';

// sessionStorage key holding the license state the user dismissed. Dismissal
// is per browser session on purpose: a warning that was waved away should
// come back the next time the operator opens the dashboard.
export const LICENSE_BANNER_DISMISSED_KEY = 'hermetiq.licenseBanner.dismissed';

// Suppress the unlicensed/validating info banner for the first couple of
// minutes after the dashboard loads — normal first-boot trial issuance passes
// through those states and should not flash a banner at the user.
export const LICENSE_STARTUP_GRACE_MS = 2 * 60 * 1000;

export const LICENSE_LAPSED_MESSAGE =
  'The Hermetiq license has lapsed and the service is no longer serving requests. ' +
  'Data is retained. Contact support@hermetiq.com.';

const LAPSED_STATES = ['expired_grace', 'invalid', 'gated'];

const TONE_STYLES = {
  info: {
    background: 'rgba(79, 140, 255, 0.12)',
    borderColor: 'rgba(79, 140, 255, 0.45)',
    accent: 'var(--hmq-blue, #4f8cff)',
    icon: faCircleInfo,
    role: 'status',
  },
  warning: {
    background: 'rgba(249, 115, 22, 0.12)',
    borderColor: 'rgba(249, 115, 22, 0.45)',
    accent: 'var(--hmq-orange, #f97316)',
    icon: faTriangleExclamation,
    role: 'status',
  },
  error: {
    background: 'rgba(239, 68, 68, 0.16)',
    borderColor: 'rgba(239, 68, 68, 0.55)',
    accent: 'var(--hmq-red, #ef4444)',
    icon: faTriangleExclamation,
    role: 'alert',
  },
};

// Matches the formatTimestamp helpers used elsewhere (Admin, CacheEventsViewer),
// minus the time of day — license expiry only needs day precision.
const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const readDismissedState = () => {
  try {
    return window.sessionStorage.getItem(LICENSE_BANNER_DISMISSED_KEY) || '';
  } catch (error) {
    return '';
  }
};

const LicenseBanner = () => {
  const { status, fetchFailed } = useLicenseStatus();
  const [dismissedState, setDismissedState] = useState(readDismissedState);
  const mountedAtRef = useRef(Date.now());
  const [startupGraceElapsed, setStartupGraceElapsed] = useState(false);

  useEffect(() => {
    const remaining = LICENSE_STARTUP_GRACE_MS - (Date.now() - mountedAtRef.current);
    if (remaining <= 0) {
      setStartupGraceElapsed(true);
      return undefined;
    }
    const timer = setTimeout(() => setStartupGraceElapsed(true), remaining);
    return () => clearTimeout(timer);
  }, []);

  if (!isOnPremMode) return null;

  const dismiss = (state) => {
    try {
      window.sessionStorage.setItem(LICENSE_BANNER_DISMISSED_KEY, state);
    } catch (error) {
      // Storage unavailable — the in-memory dismissal still applies.
    }
    setDismissedState(state);
  };

  const renderBanner = ({ tone, text, dismissibleState }) => {
    const style = TONE_STYLES[tone];
    return (
      <div
        role={style.role}
        data-testid="license-banner"
        style={{
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          gap: '10px',
          width: '100%',
          padding: '10px 18px',
          background: style.background,
          borderBottom: `1px solid ${style.borderColor}`,
          color: 'var(--hmq-text, #f5f7fb)',
          fontSize: '13px',
          lineHeight: '1.5',
        }}
      >
        <FontAwesomeIcon icon={style.icon} style={{ color: style.accent, flexShrink: 0 }} />
        <span>{text}</span>
        {dismissibleState && (
          <button
            onClick={() => dismiss(dismissibleState)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--hmq-muted, #9aa6ba)',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '0 4px',
              marginLeft: 'auto',
              lineHeight: 1,
              flexShrink: 0,
            }}
            title="Dismiss"
          >
            ×
          </button>
        )}
      </div>
    );
  };

  // The status endpoint stopped answering. A gated deployment pulls its pods
  // out of the Service, so if the last thing we heard was a bad state, this
  // outage is almost certainly the license lapsing — say so. Any other blip is
  // covered by the app's normal error handling.
  if (fetchFailed) {
    if (status && LAPSED_STATES.includes(status.state)) {
      return renderBanner({ tone: 'error', text: LICENSE_LAPSED_MESSAGE });
    }
    return null;
  }

  if (!status) return null;

  switch (status.state) {
    case 'unlicensed':
    case 'validating': {
      if (!startupGraceElapsed) return null;
      if (dismissedState === status.state) return null;
      if (!status.message) return null;
      return renderBanner({ tone: 'info', text: status.message, dismissibleState: status.state });
    }
    case 'expiring_soon': {
      if (dismissedState === status.state) return null;
      const days = status.daysRemaining;
      const expiryDate = formatDate(status.expiry);
      const text =
        `License expires in ${days} day${days === 1 ? '' : 's'}` +
        `${expiryDate ? ` (${expiryDate})` : ''}. Contact support@hermetiq.com to renew.`;
      return renderBanner({ tone: 'warning', text, dismissibleState: status.state });
    }
    case 'expired_grace':
    case 'invalid': {
      const graceEndDate = formatDate(status.graceEnd);
      const message = status.message || 'The Hermetiq license is not valid.';
      const text = `${message}${graceEndDate ? ` Dashboards become unavailable on ${graceEndDate}.` : ''}`;
      return renderBanner({ tone: 'error', text });
    }
    case 'gated': {
      // Gated is past its graceEnd, so the future-tense grace sentence would
      // be wrong here — the backend message already says what happened.
      return renderBanner({ tone: 'error', text: status.message || LICENSE_LAPSED_MESSAGE });
    }
    case 'active':
    default:
      return null;
  }
};

export default LicenseBanner;
